<template>
  <div class="resource-config">
    <!-- 触发按钮 -->
    <el-button
      type="success"
      plain
      @click="handleOpen"
      :disabled="!examId"
    >
      {{ title || '资源配置' }}
    </el-button>

    <!-- 资源配置弹窗 -->
    <el-dialog
      :title="title || '资源配置'"
      :visible.sync="dialogVisible"
      width="1000px"
      :close-on-click-modal="false"
      class="public-dialog"
      @close="handleClose"
    >
      <!-- 多语言切换标签页 -->
      <div class="language-tabs-container">
        <el-tabs
          type="card"
          v-model="currentLanguageCode"
          @tab-click="handleLanguageTabClick"
          class="language-tabs"
        >
          <el-tab-pane
            v-for="(item, index) in languageList"
            :key="index"
            :label="item.name"
            :name="item.code"
          ></el-tab-pane>
        </el-tabs>
      </div>

      <div class="resource-config-content">
        <!-- 左侧类型菜单 -->
        <div class="type-menu">
          <div class="menu-title">资源类型</div>
          <div class="menu-list">
            <div
              v-for="item in resourceTypeEnum"
              :key="item.enumVal"
              :class="['menu-item', { active: selectedType === item.enumVal }]"
              @click="selectType(item.enumVal)"
            >
              <span>{{ item.enumName }}</span>
              <el-badge
                :value="getTypeCount(item.enumVal)"
                :hidden="getTypeCount(item.enumVal) === 0"
                class="item-badge"
              />
            </div>
            <div
              :class="['menu-item', { active: selectedType === 'all' }]"
              @click="selectType('all')"
            >
              <span>全部</span>
              <el-badge
                :value="resourceTableData.length"
                :hidden="resourceTableData.length === 0"
                class="item-badge"
              />
            </div>
          </div>
        </div>

        <!-- 右侧表格内容 -->
        <div class="table-content">
          <div class="content-header">
            <span class="current-type">
              {{ getCurrentTypeName() }}
            </span>
            <el-button
              type="primary"
              size="small"
              @click="handleAdd"
              :disabled="selectedType === 'all'"
            >
              添加资源
            </el-button>
          </div>

          <div class="public-table-data">
            <el-table
              ref="resourceTable"
              size="medium"
              :data="filteredTableData"
              border
              style="width: 100%"
              row-key="id"
              v-loading="loading"
              element-loading-text="等待接口数据返回中······"
            >
              <el-table-column prop="type" label="类型" width="120" align="center">
                <template slot-scope="scope">
                  <span>{{ getTypeName(scope.row.type) }}</span>
                </template>
              </el-table-column>

              <el-table-column prop="code" label="编码" width="120" align="center">
                <template slot-scope="scope">
                  <el-input
                    v-model="scope.row.code"
                    size="mini"
                    placeholder="编号"
                    :disabled="scope.row.isExisting"
                  ></el-input>
                </template>
              </el-table-column>

              <el-table-column prop="icon" label="图标" width="65" align="center">
                <template slot-scope="scope">
                  <UploadCommon
                      simple
                      v-model="scope.row.icon"
                      :disabled="loading"
                      :size="40"
                  />
                </template>
              </el-table-column>

              <el-table-column prop="value" label="值"  align="center">
                <template slot-scope="scope">
                  <el-input
                    v-model="scope.row.value"
                    size="mini"
                    placeholder="请输入值"
                    @keyup.enter="handleSave(scope.row)"
                  ></el-input>
                </template>
              </el-table-column>


              <el-table-column label="操作" align="center" fixed="right" width="150">
                <template slot-scope="scope">
                  <el-button
                    size="mini"
                    @click="handleSave(scope.row)"
                    type="success"
                    plain
                    :loading="scope.row.saving"
                  >
                    保存
                  </el-button>
                  <el-button
                    size="mini"
                    @click="handleDelete(scope.row, scope.$index)"
                    type="danger"
                    plain
                    :loading="scope.row.deleting"
                  >
                    删除
                  </el-button>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import UploadCommon from '@comp/UploadCommon.vue';

export default {
  name: 'ResourceConfig',
  components: {
    UploadCommon,
  },
  props: {
    // 弹窗标题
    title: {
      type: String,
      default: '资源配置'
    },
    // 测评ID
    examId: {
      type: [String, Number],
      required: true
    },
    // 类型代码，用于获取资源类型枚举
    typeCode: {
      type: String,
      default: 'XLSY_STATIC'
    }
  },
  data() {
    return {
      dialogVisible: false,
      loading: false,
      resourceTableData: [],
      resourceTypeEnum: [],
      selectedType: 'all', // 当前选中的类型
      languageList: [], // 语言列表
      currentLanguageCode: 'CN' // 当前选中的语言代码
    };
  },
  computed: {
    // 根据选中类型过滤表格数据
    filteredTableData() {
      if (this.selectedType === 'all') {
        return this.resourceTableData;
      }
      return this.resourceTableData.filter(item => item.type === this.selectedType);
    }
  },
  methods: {
    // 打开弹窗
    async handleOpen() {
      if (!this.examId) {
        this.$message.warning('examId不能为空');
        return;
      }

      this.dialogVisible = true;
      this.selectedType = 'all'; // 重置为全部
      await this.getLanguageList();
      await this.getResourceTypeEnum();
      await this.getResourceList();
    },
    
    // 关闭弹窗
    handleClose() {
      this.dialogVisible = false;
      this.$emit('close');
    },

    // 获取语言列表
    async getLanguageList() {
      if (this.languageList.length > 0) return; // 已获取则直接返回

      try {
        const { data: res } = await this.$post('/language/list', {
          adminStatus: 1
        });

        if (res.result === 200 && res.data) {
          this.languageList = res.data;
          // 查找中文简体，代码为CN
          const zhCN = res.data.find(item => item.code === 'CN');
          this.currentLanguageCode = zhCN ? zhCN.code : res.data[0].code;
        }
      } catch (error) {
        console.error('获取语言列表失败:', error);
        this.$message.error('获取语言列表失败');
      }
    },

    // 处理语言切换
    async handleLanguageTabClick(tab) {
      this.currentLanguageCode = tab.name;
      // 切换语言后重新获取资源列表
      await this.getResourceList();
    },
    
    // 获取资源类型字典
    async getResourceTypeEnum() {
      if (this.resourceTypeEnum.length > 0) return;
      
      this.loading = true;
      try {
        const { data: res } = await this.$get(`/enums/list/${this.typeCode}`);
        if (res.result === 200) {
          this.resourceTypeEnum = res.data.map(item => ({
            enumVal: item.enumVal,
            enumName: item.enumName
          }));
        }
      } catch (error) {
        console.error('获取资源类型失败:', error);
        this.$message.error('获取资源类型失败');
      } finally {
        this.loading = false;
      }
    },
    
    // 获取资源列表
    async getResourceList() {
      this.loading = true;
      try {
        const { data: res } = await this.$get(`/exam/static/resource/info/${this.examId}`, {
          headers: {
            'Language-Code': this.currentLanguageCode
          }
        });
        if (res.result === 200) {
          this.resourceTableData = (res.data || []).map(item => ({
            ...item,
            isExisting: true, // 标记为已存在的数据
            saving: false,
            deleting: false
          }));
        }
      } catch (error) {
        console.error('获取资源列表失败:', error);
        this.$message.error('获取资源列表失败');
      } finally {
        this.loading = false;
      }
    },
    
    // 选择类型
    selectType(type) {
      this.selectedType = type;
    },

    // 获取类型名称
    getTypeName(type) {
      const typeItem = this.resourceTypeEnum.find(item => item.enumVal === type);
      return typeItem ? typeItem.enumName : type;
    },

    // 获取当前类型名称
    getCurrentTypeName() {
      if (this.selectedType === 'all') {
        return '全部资源';
      }
      return this.getTypeName(this.selectedType);
    },

    // 获取指定类型的资源数量
    getTypeCount(type) {
      return this.resourceTableData.filter(item => item.type === type).length;
    },

    // 添加资源
    handleAdd() {
      if (this.selectedType === 'all') {
        this.$message.warning('请先选择资源类型');
        return;
      }

      this.resourceTableData.push({
        code: '',
        type: this.selectedType, // 使用当前选中的类型
        value: '',
        descr: '',
        icon: '',
        isExisting: false,
        saving: false,
        deleting: false
      });
    },
    
    // 保存资源
    async handleSave(row) {
      // 验证必填字段
      if (!row.code || !row.type) {
        this.$message.warning('编码和类型为必填项');
        return;
      }
      
      row.saving = true;
      try {
        const params = {
          id: row.id,
          examId: this.examId,
          code: row.code,
          type: row.type,
          value: row.value || '',
          descr: row.descr || '',
          icon: row.icon || ''
        };
        
        const { data: res } = await this.$post('exam/static/resource/info', params, {
          headers: {
            'Language-Code': this.currentLanguageCode
          }
        });
        if (res.result === 200) {
          this.$message.success('保存成功');
          row.isExisting = true; // 标记为已存在
          this.$emit('save', { row, params, languageCode: this.currentLanguageCode });
          // 重新获取列表以确保数据同步
          await this.getResourceList();
        } else {
          this.$message.error(res.message || '保存失败');
        }
      } catch (error) {
        console.error('保存失败:', error);
        this.$message.error('保存失败');
      } finally {
        row.saving = false;
      }
    },
    
    // 删除资源
    async handleDelete(row, index) {
      if (!row.id || !row.isExisting) {
        // 如果是新增的未保存项，直接从表格中移除
        this.resourceTableData.splice(index, 1);
        this.$emit('delete', { row, index, type: 'local', languageCode: this.currentLanguageCode });
        return;
      }
      
      this.$confirm('此操作将永久删除该资源, 是否继续?', '提示', {
        type: 'warning'
      }).then(async () => {
        row.deleting = true;
        try {
          const { data: res } = await this.$delete(`exam/static/resource/info/${row.id}`);
          if (res.result === 200) {
            this.$message.success('删除成功');
            this.$emit('delete', { row, index, type: 'server', languageCode: this.currentLanguageCode });
            // 重新获取列表
            await this.getResourceList();
          } else {
            this.$message.error(res.message || '删除失败');
          }
        } catch (error) {
          console.error('删除失败:', error);
          this.$message.error('删除失败');
        } finally {
          row.deleting = false;
        }
      }).catch(() => {
        // 取消删除
      });
    }
  }
};
</script>

<style scoped>
.resource-config {
  display: inline-block;
}

.language-tabs-container {
  margin-bottom: 20px;
  border-bottom: 1px solid #e4e7ed;
  padding-bottom: 15px;
}

.language-tabs :deep(.el-tabs__header) {
  margin-bottom: 0;
}

.language-tabs :deep(.el-tabs__nav-wrap) {
  padding: 0;
}

.language-tabs :deep(.el-tabs__item) {
  padding: 0 20px;
  height: 32px;
  line-height: 32px;
  font-size: 13px;
}

.language-tabs :deep(.el-tabs__item.is-active) {
  background-color: #409eff;
  color: #ffffff;
  border-color: #409eff;
}

.language-tabs :deep(.el-tabs__item:hover) {
  color: #409eff;
}

.resource-config-content {
  display: flex;
  height: 500px;
}

.type-menu {
  width: 120px;
  border-right: 1px solid #e4e7ed;
  background-color: #fafafa;
}

.menu-title {
  padding: 15px;
  font-weight: bold;
  color: #303133;
  border-bottom: 1px solid #e4e7ed;
  background-color: #f5f7fa;
}

.menu-list {
  padding: 10px 0;
}

.menu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 15px;
  cursor: pointer;
  color: #606266;
  transition: all 0.3s;
  position: relative;
}

.menu-item:hover {
  background-color: #ecf5ff;
  color: #409eff;
}

.menu-item.active {
  background-color: #409eff;
  color: #ffffff;
}

.menu-item.active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background-color: #ffffff;
}

.item-badge {
  margin-left: 8px;
}

.menu-item.active .item-badge :deep(.el-badge__content) {
  background-color: #ffffff;
  color: #409eff;
}

.table-content {
  flex: 1;
  padding-left: 20px;
  display: flex;
  flex-direction: column;
}

.content-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e4e7ed;
}

.current-type {
  font-size: 16px;
  font-weight: bold;
  color: #303133;
}

.public-table-data {
  flex: 1;
  overflow: auto;
}
</style>
